import { TrendingUp, Users, Award, BookOpen, Bell } from 'lucide-react';
import { Timer } from './Timer';

interface HeroSectionProps {
  activeReaders: number;
  onOrderClick: () => void;
}

export function HeroSection({ activeReaders, onOrderClick }: HeroSectionProps) {
  return (
    <section className="relative min-h-[85vh] flex items-center justify-center bg-[#1a2837] px-4 sm:px-6 py-10 sm:py-16">
      {/* Background gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.1)_0%,transparent_70%)] pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/80 pointer-events-none" />
      
      {/* Live Readers Counter */}
      <div className="absolute top-2 sm:top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-red-900/20 to-black/40 backdrop-blur-sm rounded-full py-1.5 sm:py-2 px-3 sm:px-4 border border-red-500/20 z-30">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-green-500 rounded-full animate-pulse" />
          <p className="text-xs sm:text-sm text-gray-300">
            <span className="text-white font-semibold">{activeReaders}</span> cititori activi acum
          </p>
        </div>
      </div>

      <div className="container max-w-6xl relative z-20">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          <div className="flex-1 text-center lg:text-left mt-8 sm:mt-0">
            <div className="relative">
              <div className="absolute -top-6 left-1/2 lg:left-0 -translate-x-1/2 lg:translate-x-0">
                <Bell className="w-7 h-7 text-red-500 animate-bounce" />
              </div>
              <h1 className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 leading-tight">
                <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">DEPRESIA,</span><br />
                <span className="bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">SUPERPUTEREA TA!</span>
              </h1>
            </div>

            <div className="space-y-4 mb-6">
              <p className="text-base sm:text-lg lg:text-xl font-light max-w-2xl mx-auto lg:mx-0 text-gray-300">
                Descoperă sistemul dovedit care a ajutat peste 10,000 de oameni să transforme depresia în motivație și putere interioară.
              </p>
              <div className="flex flex-col gap-2 text-sm sm:text-base text-gray-400">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  <p>27 exerciții practice + 5 tehnici de urgență pentru atacuri de panică</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  <p>Sistem dovedit de transformare în 5 pași simpli</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  <p>Rezultate vizibile în primele 30 de zile sau banii înapoi</p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-black/40 to-black/60 p-3 sm:p-4 rounded-xl border border-red-500/20">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <TrendingUp className="w-5 h-5 text-red-500" />
                  <span className="text-lg sm:text-xl font-bold text-white">98%</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-400">Rată de Succes</p>
              </div>
              <div className="bg-gradient-to-br from-black/40 to-black/60 p-3 sm:p-4 rounded-xl border border-red-500/20">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Users className="w-5 h-5 text-red-500" />
                  <span className="text-lg sm:text-xl font-bold text-white">10K+</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-400">Vieți Transformate</p>
              </div>
              <div className="bg-gradient-to-br from-black/40 to-black/60 p-3 sm:p-4 rounded-xl border border-red-500/20">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Award className="w-5 h-5 text-red-500" />
                  <span className="text-lg sm:text-xl font-bold text-white">30</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-400">Zile Garanție</p>
              </div>
            </div>

            <Timer />

            <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-md p-3 sm:p-4 rounded-xl mb-4 sm:mb-6 border border-white/10 hover:border-white/20 transition-all duration-300">
              <p className="text-sm sm:text-base">
                De la Andrei Ungureanu (@omulcutourette) - câștigător iUmor și creator cu peste 1.2 milioane de urmăritori, care a transformat propria luptă cu depresia în misiunea de a ajuta și alții.
              </p>
            </div>
            
            <button 
              onClick={() => {
                console.log('Order button clicked');
                onOrderClick();
              }}
              className="relative w-full sm:w-auto mt-4 bg-gradient-to-r from-red-600 to-red-700 text-white text-base sm:text-lg px-6 sm:px-8 py-2 sm:py-3 rounded-full font-bold transform transition-all duration-500 hover:scale-105 flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(239,68,68,0.3)] hover:shadow-[0_0_50px_rgba(239,68,68,0.5)] z-50"
              aria-label="Transformă-ți viața acum"
            >
              <span>TRANSFORMĂ-ȚI VIAȚA ACUM</span> <BookOpen className="w-5 h-5" />
            </button>
          </div>
          
          {/* Book Cover */}
          <div className="w-[280px] sm:w-[320px] lg:w-[400px] mx-auto">
            <img 
              src="/book-cover.jpg"
              alt="Cartea Depresia, Superputerea Ta!"
              className="w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transition-all duration-500 hover:shadow-[0_0_50px_rgba(239,68,68,0.3)]"
              width={400}
              height={400}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
